import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Loader2, ShieldCheck } from 'lucide-react';
import Logo from '../components/Logo';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast({ title: `Welcome, ${res.user.name}`, description: 'Signed in successfully.' });
      navigate(res.user.role === 'super_user' ? '/app/admin' : '/app/dashboard');
    } else {
      toast({ title: 'Sign in failed', description: res.error, variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #1e3a8a 0%, #2563eb 55%, #0891b2 100%)' }}>
        <Logo size={40} withText light />
        <div className="relative z-10">
          <h2 className="font-display font-extrabold text-4xl leading-tight max-w-md">
            One secure platform for every pharmacy in Uganda.
          </h2>
          <p className="mt-5 text-blue-100 text-lg max-w-md">
            Manage prescriptions, FEFO batches, dispensing and billing — with
            role-based access for your whole team.
          </p>
          <div className="mt-8 flex items-center gap-2 text-blue-100">
            <ShieldCheck size={20} />
            <span className="text-sm font-medium">Secure role-based access · Low-bandwidth ready</span>
          </div>
        </div>
        <div className="text-blue-200 text-sm relative z-10">© 2026 Vitality Software Limited</div>
        <div className="absolute -right-20 -bottom-20 h-80 w-80 rounded-full bg-white/10" />
        <div className="absolute right-24 top-24 h-40 w-40 rounded-full bg-white/10" />
      </div>

      {/* Right form */}
      <div className="flex flex-col justify-center px-6 sm:px-16 py-10">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm font-medium mb-10 w-fit transition-colors">
          <ArrowLeft size={16} /> Back to home
        </button>
        <div className="lg:hidden mb-8"><Logo size={38} /></div>
        <h1 className="font-display font-extrabold text-3xl text-slate-900">Sign in</h1>
        <p className="text-slate-500 mt-2">Access your Vitality Pharmacy workspace.</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5 max-w-md">
          <div>
            <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@pharmacy.ug" required
              className="mt-2 h-12 rounded-lg border-slate-300" />
          </div>
          <div>
            <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" required
              className="mt-2 h-12 rounded-lg border-slate-300" />
          </div>
          <Button type="submit" disabled={loading}
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[16px] font-semibold">
            {loading ? <Loader2 className="animate-spin" size={18} /> : 'Sign in'}
          </Button>
        </form>

        <div className="mt-8 max-w-md">
          <p className="text-xs text-slate-400">
            Access is provided by your platform administrator. If you can't sign in, your pharmacy account may be suspended — please contact your administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
